/**
 * AI Tutor configuration API client and React Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiDelete, apiGet, apiPost, apiPut } from 'services/api';
import logger from 'utils/logger';

const TutorConfigurationSchema = z.object({
  id: z.string().uuid(),
  nativeLanguageCode: z.string().min(1),
  targetLanguageCode: z.string().min(1),
  voiceId: z.number().int().positive(),
  explainerVoiceId: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const TutorConfigurationDeletedSchema = z.object({
  deleted: z.literal(true),
});

export type TutorConfiguration = z.infer<typeof TutorConfigurationSchema>;

export interface TutorConfigurationBody {
  nativeLanguageCode: string;
  targetLanguageCode: string;
  voiceId: number;
  explainerVoiceId: number;
}

const LanguagePairConfigurationSchema = z.object({
  nativeLanguageCode: z.string().min(1),
  targetLanguageCode: z.string().min(1),
  freePracticeEnabled: z.boolean(),
  voiceAssessmentEnabled: z.boolean(),
  assignedAiTutorConfigurationId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type LanguagePairConfiguration = z.infer<typeof LanguagePairConfigurationSchema>;

export interface LanguagePairConfigurationBody {
  nativeLanguageCode: string;
  targetLanguageCode: string;
  freePracticeEnabled: boolean;
  voiceAssessmentEnabled: boolean;
  assignedAiTutorConfigurationId: string | null;
}

const tutorConfigurationKeys = {
  all: ['ai-tutor-configurations'] as const,
  pairs: ['ai-tutor-configurations', 'language-pairs'] as const,
};

function transformSnakeToCamel(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()),
      value,
    ])
  );
}

function toRequestBody(body: TutorConfigurationBody): Record<string, unknown> {
  return {
    native_language_code: body.nativeLanguageCode,
    target_language_code: body.targetLanguageCode,
    voice_id: body.voiceId,
    explainer_voice_id: body.explainerVoiceId,
  };
}

function toPairRequestBody(body: LanguagePairConfigurationBody): Record<string, unknown> {
  return {
    native_language_code: body.nativeLanguageCode,
    target_language_code: body.targetLanguageCode,
    free_practice_enabled: body.freePracticeEnabled,
    voice_assessment_enabled: body.voiceAssessmentEnabled,
    assigned_ai_tutor_configuration_id: body.assignedAiTutorConfigurationId,
  };
}

function parseConfiguration(data: unknown): TutorConfiguration {
  const record = z.record(z.unknown()).parse(data);
  return TutorConfigurationSchema.parse(transformSnakeToCamel(record));
}

async function fetchTutorConfigurations(): Promise<TutorConfiguration[]> {
  try {
    const data = await apiGet<unknown>('/api/admin/ai-tutor-configurations');
    return z.array(z.unknown()).parse(data).map(parseConfiguration);
  } catch (error) {
    logger.error('Error fetching AI Tutor configurations:', error);
    throw error;
  }
}

async function createTutorConfiguration(body: TutorConfigurationBody): Promise<TutorConfiguration> {
  try {
    const data = await apiPost<unknown>('/api/admin/ai-tutor-configurations', toRequestBody(body));
    return parseConfiguration(data);
  } catch (error) {
    logger.error('Error creating AI Tutor configuration:', error);
    throw error;
  }
}

async function replaceTutorConfiguration({
  id,
  body,
}: {
  id: string;
  body: TutorConfigurationBody;
}): Promise<TutorConfiguration> {
  try {
    const data = await apiPut<unknown>(
      `/api/admin/ai-tutor-configurations/${id}`,
      toRequestBody(body)
    );
    return parseConfiguration(data);
  } catch (error) {
    logger.error('Error replacing AI Tutor configuration:', error);
    throw error;
  }
}

async function deleteTutorConfiguration(id: string): Promise<void> {
  try {
    const data = await apiDelete<unknown>(`/api/admin/ai-tutor-configurations/${id}`);
    TutorConfigurationDeletedSchema.parse(data);
  } catch (error) {
    logger.error('Error deleting AI Tutor configuration:', error);
    throw error;
  }
}

async function fetchLanguagePairConfigurations(): Promise<LanguagePairConfiguration[]> {
  const data = await apiGet<unknown>('/api/admin/ai-tutor-configurations/language-pairs');
  return z
    .array(z.unknown())
    .parse(data)
    .map((row) =>
      LanguagePairConfigurationSchema.parse(transformSnakeToCamel(z.record(z.unknown()).parse(row)))
    );
}

async function putLanguagePairConfiguration(
  body: LanguagePairConfigurationBody
): Promise<LanguagePairConfiguration> {
  const data = await apiPut<unknown>(
    '/api/admin/ai-tutor-configurations/language-pairs',
    toPairRequestBody(body)
  );
  return LanguagePairConfigurationSchema.parse(
    transformSnakeToCamel(z.record(z.unknown()).parse(data))
  );
}

async function deleteLanguagePairConfiguration({
  nativeLanguageCode,
  targetLanguageCode,
}: Pick<
  LanguagePairConfigurationBody,
  'nativeLanguageCode' | 'targetLanguageCode'
>): Promise<void> {
  const query = new URLSearchParams({
    native_language_code: nativeLanguageCode,
    target_language_code: targetLanguageCode,
  });
  const data = await apiDelete<unknown>(
    `/api/admin/ai-tutor-configurations/language-pairs?${query.toString()}`
  );
  TutorConfigurationDeletedSchema.parse(data);
}

export function useTutorConfigurations() {
  return useQuery({
    queryKey: tutorConfigurationKeys.all,
    queryFn: fetchTutorConfigurations,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateTutorConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTutorConfiguration,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tutorConfigurationKeys.all }),
  });
}

export function useReplaceTutorConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: replaceTutorConfiguration,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tutorConfigurationKeys.all }),
  });
}

export function useLanguagePairConfigurations() {
  return useQuery({
    queryKey: tutorConfigurationKeys.pairs,
    queryFn: fetchLanguagePairConfigurations,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePutLanguagePairConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putLanguagePairConfiguration,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tutorConfigurationKeys.pairs });
    },
  });
}

export function useDeleteLanguagePairConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteLanguagePairConfiguration,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tutorConfigurationKeys.pairs });
    },
  });
}

export function useDeleteTutorConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTutorConfiguration,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tutorConfigurationKeys.all }),
  });
}
